import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, FileText } from "lucide-react";

export default function AdminPersonalShopper() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("quotes"); // quotes | settings
  const [settings, setSettings] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      try {
        const [s, q] = await Promise.all([
          api.get("/personal-shopper/settings"),
          api.get("/admin/personal-shopper/quotes"),
        ]);
        setSettings(s.data);
        setQuotes(q.data);
      } catch (e) {
        toast.error("Erreur chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/connexion"/>;
  if (user.role !== "admin") return <Navigate to="/"/>;

  const updateStatus = async (qid, status) => {
    try {
      await api.patch(`/admin/personal-shopper/quotes/${qid}/status`, { status });
      setQuotes((qs) => qs.map((q) => q.id === qid ? { ...q, status } : q));
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error("Erreur mise à jour");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/personal-shopper/settings", settings);
      setSettings(data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const updRate = (k, v) => setSettings((s) => ({ ...s, rates: { ...(s.rates || {}), [k]: parseFloat(v) || 0 } }));
  const updField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const updTier = (i, k, v) => setSettings((s) => {
    const tiers = [...(s.commission_tiers || [])];
    tiers[i] = { ...tiers[i], [k]: v === "" ? null : parseFloat(v) };
    return { ...s, commission_tiers: tiers };
  });
  const updZone = (i, k, v) => setSettings((s) => {
    const zones = [...(s.kinshasa_zones || [])];
    zones[i] = { ...zones[i], [k]: k === "fee_cdf" ? (v === "" ? null : parseFloat(v)) : v };
    return { ...s, kinshasa_zones: zones };
  });
  const updScenario = (i, k, v) => setSettings((s) => {
    const scen = [...(s.scenarios || [])];
    scen[i] = { ...scen[i], [k]: v };
    return { ...s, scenarios: scen };
  });

  if (loading || !settings) return <div className="max-w-5xl mx-auto py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-ps-page">
      <Button variant="ghost" onClick={() => nav("/admin")} className="mb-4" data-testid="admin-ps-back">
        <ArrowLeft className="w-4 h-4 mr-1"/> Retour au tableau de bord admin
      </Button>
      <h1 className="heading font-extrabold text-3xl text-[#111812] mb-2">Personal Shopper</h1>
      <p className="text-[#4B5563] mb-6">Devis générés par les clients + paramètres du calculateur.</p>

      <div className="inline-flex bg-[#F5F5F0] p-1 rounded-full mb-6">
        <button onClick={() => setTab("quotes")} className={`px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "quotes" ? "bg-[#1A3322] text-white" : "text-[#4B5563]"}`} data-testid="admin-ps-tab-quotes">
          <FileText className="w-4 h-4 inline mr-1.5"/> Devis ({quotes.length})
        </button>
        <button onClick={() => setTab("settings")} className={`px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "settings" ? "bg-[#1A3322] text-white" : "text-[#4B5563]"}`} data-testid="admin-ps-tab-settings">
          Paramètres
        </button>
      </div>

      {tab === "quotes" && (
        <Card data-testid="admin-ps-quotes">
          <CardHeader><CardTitle>Devis reçus</CardTitle></CardHeader>
          <CardContent>
            {quotes.length === 0 ? (
              <p className="text-[#4B5563] text-sm">Aucun devis pour l'instant.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-[#1A3322]/10">
                      <th className="py-2 pr-3">N°</th>
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Client</th>
                      <th className="py-2 pr-3">Scénario</th>
                      <th className="py-2 pr-3 text-right">Achats</th>
                      <th className="py-2 pr-3 text-right">Total</th>
                      <th className="py-2 pr-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.id} className="border-b border-[#1A3322]/5" data-testid={`admin-ps-quote-${q.quote_number}`}>
                        <td className="py-2 pr-3 font-mono font-bold text-[#C85A32]">{q.quote_number}</td>
                        <td className="py-2 pr-3 text-xs">{new Date(q.created_at).toLocaleDateString("fr-FR")}</td>
                        <td className="py-2 pr-3">
                          <div className="font-semibold">{q.full_name}</div>
                          <div className="text-xs text-[#4B5563]">{q.email}</div>
                        </td>
                        <td className="py-2 pr-3 text-xs">{q.scenario}</td>
                        <td className="py-2 pr-3 text-right">${q.purchase_amount_usd?.toFixed(2)}</td>
                        <td className="py-2 pr-3 text-right font-bold">${q.total_usd?.toFixed(2)}</td>
                        <td className="py-2 pr-3">
                          <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                            <SelectTrigger className="h-8 w-32"><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Nouveau</SelectItem>
                              <SelectItem value="validated">Validé</SelectItem>
                              <SelectItem value="ordered">Commandé</SelectItem>
                              <SelectItem value="rejected">Rejeté</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "settings" && (
        <div className="space-y-6" data-testid="admin-ps-settings">
          <Card>
            <CardHeader><CardTitle>Taux & fret</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1.5"><Label>USD → EUR</Label>
                  <Input type="number" step="0.0001" value={settings.rates?.USD_EUR || ""} onChange={(e) => updRate("USD_EUR", e.target.value)} data-testid="admin-ps-usd-eur"/>
                </div>
                <div className="space-y-1.5"><Label>USD → CDF</Label>
                  <Input type="number" step="0.01" value={settings.rates?.USD_CDF || ""} onChange={(e) => updRate("USD_CDF", e.target.value)} data-testid="admin-ps-usd-cdf"/>
                </div>
                <div className="space-y-1.5"><Label>Fret / kg (USD)</Label>
                  <Input type="number" step="0.01" value={settings.freight_per_kg_usd || ""} onChange={(e) => updField("freight_per_kg_usd", parseFloat(e.target.value) || 0)} data-testid="admin-ps-fpk"/>
                </div>
                <div className={`space-y-1.5 p-2 -m-2 rounded-lg ${(settings.france_freight_per_kg_usd == null || settings.france_freight_per_kg_usd === "") ? "bg-yellow-50 border border-yellow-300" : ""}`}>
                  <Label>Livraison Congo → France / kg (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={settings.france_freight_per_kg_usd ?? ""}
                    onChange={(e) => updField("france_freight_per_kg_usd", e.target.value === "" ? null : parseFloat(e.target.value))}
                    placeholder="À définir"
                    data-testid="admin-ps-france-fpk"
                  />
                  <p className="text-[10px] text-yellow-800">{(settings.france_freight_per_kg_usd == null || settings.france_freight_per_kg_usd === "") ? "⚠️ Vide — devis France affiche 0 $" : ""}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Paliers de commission</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(settings.commission_tiers || []).map((t, i) => (
                <div key={i} className="grid grid-cols-3 gap-3 items-end">
                  <div><Label className="text-xs">Min $</Label>
                    <Input type="number" step="0.01" value={t.min_usd ?? ""} onChange={(e) => updTier(i, "min_usd", e.target.value)}/>
                  </div>
                  <div><Label className="text-xs">Max $ (vide = ∞)</Label>
                    <Input type="number" step="0.01" value={t.max_usd ?? ""} onChange={(e) => updTier(i, "max_usd", e.target.value)}/>
                  </div>
                  <div><Label className="text-xs">Commission %</Label>
                    <Input type="number" step="0.1" value={t.pct ?? ""} onChange={(e) => updTier(i, "pct", e.target.value)}/>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Zones Kinshasa (FC)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(settings.kinshasa_zones || []).map((z, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div><Label className="text-xs">Zone {z.id} — communes</Label>
                    <Input value={(z.communes || []).join(", ")} onChange={(e) => updZone(i, "communes", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}/>
                  </div>
                  <div><Label className="text-xs">Frais (FC, vide = à définir)</Label>
                    <Input type="number" value={z.fee_cdf ?? ""} onChange={(e) => updZone(i, "fee_cdf", e.target.value)}/>
                  </div>
                  <div><Label className="text-xs">Note (facultative)</Label>
                    <Input value={z.note_fr || ""} onChange={(e) => updZone(i, "note_fr", e.target.value)}/>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Scénarios</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(settings.scenarios || []).map((s, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div><Label className="text-xs">ID</Label>
                    <Input value={s.id} disabled/>
                  </div>
                  <div><Label className="text-xs">Libellé FR</Label>
                    <Input value={s.label_fr || ""} onChange={(e) => updScenario(i, "label_fr", e.target.value)}/>
                  </div>
                  <div><Label className="text-xs">Note FR</Label>
                    <Input value={s.note_fr || ""} onChange={(e) => updScenario(i, "note_fr", e.target.value)}/>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={!!s.active} onChange={(e) => updScenario(i, "active", e.target.checked)} data-testid={`admin-ps-scen-active-${s.id}`}/>
                    <Label className="text-xs">Actif</Label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Button onClick={saveSettings} disabled={saving} className="pill-btn bg-[#1A3322] rounded-full h-12 px-6 font-bold" data-testid="admin-ps-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Enregistrement…</> : <><Save className="w-4 h-4 mr-2"/>Enregistrer les paramètres</>}
          </Button>
        </div>
      )}
    </div>
  );
}
